import { Input } from '@aibi/ui';
import { Search } from 'lucide-react';
import { useMemo } from 'react';
import { searchFields } from '../../model/field-catalog';
import { useAddField, useSelectedCard } from '../../state/use-selected-card';
import { useUiDispatch, useUiState } from '../../state/ui-state';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';
import { ExplorerSection } from './ExplorerSection';
import { DimensionList, MeasureList } from './FieldList';
import { FilterList } from './FilterList';
import { SelectionList } from './SelectionList';
import { VisualizationPicker } from './VisualizationPicker';

/** Panel izquierdo: busqueda, dimensiones, medidas, filtros, selecciones y tipos. */
export function FieldExplorer() {
  const { audience, catalog, document } = useWorkspace();
  const reportViewer = audience === 'viewer';
  const { fieldSearch } = useUiState();
  const uiDispatch = useUiDispatch();
  const selectedCard = useSelectedCard();
  const addField = useAddField();
  const { execute } = useWorkspaceActions();
  const removeField = (key: string) => {
    if (selectedCard !== null) execute({ type: 'remove-field', cardId: selectedCard.id, key });
  };
  const dimensions = useMemo(
    () => searchFields(catalog.dimensions, fieldSearch),
    [catalog, fieldSearch],
  );
  const measures = useMemo(
    () => searchFields(catalog.measures, fieldSearch),
    [catalog, fieldSearch],
  );
  const selections = (selectedCard ?? document.cards[0])?.selections ?? [];

  return (
    <aside
      aria-label="Explorador de campos"
      className="flex h-full w-64 shrink-0 flex-col border-r border-border bg-sidebar"
    >
      <div className="border-b border-border p-2">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label="Buscar campos"
            placeholder="Buscar campos..."
            value={fieldSearch}
            className="pl-7"
            onChange={(event) => {
              uiDispatch({ type: 'set-field-search', query: event.target.value });
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-1 pb-4">
        <DimensionList
          fields={dimensions}
          selectedCard={selectedCard}
          onAdd={addField}
          onRemove={removeField}
        />
        <MeasureList
          fields={measures}
          selectedCard={selectedCard}
          onAdd={addField}
          onRemove={removeField}
        />
        {!reportViewer && <FilterList card={selectedCard} />}
        {!reportViewer && (
          <ExplorerSection title="Selecciones" count={selections.length}>
            <SelectionList selections={selections} />
          </ExplorerSection>
        )}
        <ExplorerSection title="Visualizaciones">
          <VisualizationPicker card={selectedCard} />
        </ExplorerSection>
      </div>
    </aside>
  );
}
